import { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BookOpen,
  Clock,
  Play,
  Trophy,
  Search,
  CheckCircle2,
  AlertCircle,
  XCircle,
  ChevronRight,
  MoreVertical,
  Radio,
  Video,
  Calendar,
} from 'lucide-react';
import { enrollmentsApi, EnrollmentData, meetingsApi, MeetingData } from '../../lib/api';
import { formatMinutes, formatRelativeTime, getLevelColor, getStatusColor, debounce } from '../../lib/utils';
import { SectionLoader, InlineLoader } from '../../components/ui/PremiumLoader';
import { joinMeetingSecure, buildJitsiJoinUrl } from '../../lib/jitsi';
import toast from 'react-hot-toast';
import { useAnalytics, AnalyticsEvents } from '../../hooks/useAnalytics';

// Extended enrollment with live meeting info
interface EnrollmentWithMeeting extends EnrollmentData {
  activeMeeting?: MeetingData;
}

type TabStatus = 'all' | 'ACTIVE' | 'COMPLETED' | 'CANCELLED';

interface CancelModalProps {
  enrollment: EnrollmentData;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  isLoading: boolean;
}

function CancelModal({ enrollment, onClose, onConfirm, isLoading }: CancelModalProps) {
  const enrolledAt = new Date(enrollment.enrolledAt);
  const now = new Date();
  const hoursRemaining = Math.max(0, 24 - ((now.getTime() - enrolledAt.getTime()) / (1000 * 60 * 60)));
  const hasStarted = Number(enrollment.progress) > 0;
  const canCancel = hoursRemaining > 0 && !hasStarted;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
        className="relative w-full max-w-md rounded-2xl overflow-hidden"
        style={{
          background: 'linear-gradient(180deg, #18181f 0%, #111116 100%)',
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-rose-500/15 flex items-center justify-center">
              <AlertCircle className="w-6 h-6 text-rose-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Cancel Enrollment</h3>
              <p className="text-sm text-white/40">This action cannot be undone</p>
            </div>
          </div>

          <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.04] mb-4">
            <p className="text-white/70 text-sm">
              Are you sure you want to cancel your enrollment in{' '}
              <span className="text-white font-medium">{enrollment.course.title}</span>?
            </p>
          </div>

          {canCancel ? (
            <div className="flex items-center gap-2 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 mb-6">
              <Clock className="w-4 h-4 text-amber-400 flex-shrink-0" />
              <p className="text-amber-400 text-sm">
                {hoursRemaining.toFixed(1)} hours remaining to cancel
              </p>
            </div>
          ) : hasStarted ? (
            <div className="flex items-center gap-2 p-3 rounded-xl bg-rose-500/10 border border-rose-500/20 mb-6">
              <XCircle className="w-4 h-4 text-rose-400 flex-shrink-0" />
              <p className="text-rose-400 text-sm">
                Cannot cancel after starting the course
              </p>
            </div>
          ) : (
            <div className="flex items-center gap-2 p-3 rounded-xl bg-rose-500/10 border border-rose-500/20 mb-6">
              <XCircle className="w-4 h-4 text-rose-400 flex-shrink-0" />
              <p className="text-rose-400 text-sm">
                Cancellation window has expired (24 hours)
              </p>
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 px-4 py-3 rounded-xl font-medium text-white/70 
                       bg-white/[0.04] border border-white/[0.06]
                       hover:bg-white/[0.08] transition-colors"
            >
              Keep Enrolled
            </button>
            <button
              onClick={onConfirm}
              disabled={isLoading || !canCancel}
              className="flex-1 px-4 py-3 rounded-xl font-medium text-white 
                       bg-rose-500/80 hover:bg-rose-500 
                       disabled:opacity-50 disabled:cursor-not-allowed
                       transition-colors flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <InlineLoader />
                  <span className="ml-2">Cancelling...</span>
                </>
              ) : (
                'Cancel Enrollment'
              )}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

interface CourseMenuProps {
  enrollment: EnrollmentWithMeeting;
  onCancel: () => void;
  isOpen: boolean;
  onToggle: () => void;
}

function CourseMenu({ enrollment, onCancel, isOpen, onToggle }: CourseMenuProps) {
  const enrolledAt = new Date(enrollment.enrolledAt);
  const now = new Date();
  const hasStarted = Number(enrollment.progress) > 0;
  const canCancel = enrollment.status === 'ACTIVE' && 
    ((now.getTime() - enrolledAt.getTime()) / (1000 * 60 * 60)) <= 24 &&
    !hasStarted;
  const isLiveCourse = enrollment.course.courseType === 'LIVE';
  const activeMeeting = enrollment.activeMeeting;
  const isLive = activeMeeting?.status === 'LIVE';

  const handleJoinMeeting = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (activeMeeting) {
      try {
        await joinMeetingSecure(activeMeeting.id);
      } catch {
        window.open(buildJitsiJoinUrl(activeMeeting.jitsiRoomId), '_blank');
      }
      onToggle();
    }
  };

  return (
    <div className="relative">
      <button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onToggle();
        }}
        className="p-2 rounded-lg hover:bg-white/[0.06] transition-colors"
      >
        <MoreVertical className="w-4 h-4 text-white/40" />
      </button>

      <AnimatePresence>
        {isOpen && (
          <>
            <div className="fixed inset-0 z-40" onClick={onToggle} />
            <motion.div
              initial={{ opacity: 0, y: 8, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 8, scale: 0.95 }}
              transition={{ duration: 0.15 }}
              className="absolute right-0 top-full mt-1 w-48 z-50 rounded-xl overflow-hidden"
              style={{
                background: 'linear-gradient(180deg, #18181f 0%, #111116 100%)',
                border: '1px solid rgba(255,255,255,0.08)',
                boxShadow: '0 20px 50px rgba(0,0,0,0.5)',
              }}
            >
              <div className="p-1.5">
                {/* Join Meeting option for live courses with active meeting */}
                {isLive && activeMeeting && (
                  <button
                    onClick={handleJoinMeeting}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-lg 
                             text-sm font-medium text-red-400 hover:bg-red-500/10 
                             transition-colors"
                  >
                    <Radio className="w-4 h-4" />
                    Join Live Meeting
                  </button>
                )}
                {isLiveCourse ? (
                  <Link
                    to={`/courses/${enrollment.course.slug}`}
                    onClick={(e) => e.stopPropagation()}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg 
                             text-sm text-white/70 hover:text-white hover:bg-white/[0.04] 
                             transition-colors"
                  >
                    <Calendar className="w-4 h-4" />
                    View Sessions
                  </Link>
                ) : (
                  <Link
                    to={`/student/course/${enrollment.id}`}
                    onClick={(e) => e.stopPropagation()}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg 
                             text-sm text-white/70 hover:text-white hover:bg-white/[0.04] 
                             transition-colors"
                  >
                    <Play className="w-4 h-4" />
                    Continue Learning
                  </Link>
                )}
                <Link
                  to={`/courses/${enrollment.course.slug}`}
                  onClick={(e) => e.stopPropagation()}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg 
                           text-sm text-white/70 hover:text-white hover:bg-white/[0.04] 
                           transition-colors"
                >
                  <BookOpen className="w-4 h-4" />
                  View Course Details
                </Link>
                {canCancel && (
                  <>
                    <div className="my-1 border-t border-white/[0.06]" />
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onCancel();
                        onToggle();
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 rounded-lg 
                               text-sm text-rose-400 hover:bg-rose-500/10 
                               transition-colors"
                    >
                      <XCircle className="w-4 h-4" />
                      Cancel Enrollment
                    </button>
                  </>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function MyCoursesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { trackEvent } = useAnalytics();
  const [enrollments, setEnrollments] = useState<EnrollmentWithMeeting[]>([]);
  const [filteredEnrollments, setFilteredEnrollments] = useState<EnrollmentWithMeeting[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabStatus>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [cancelModal, setCancelModal] = useState<EnrollmentWithMeeting | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [joiningMeetingId, setJoiningMeetingId] = useState<string | null>(null);

  // Check for enrollment success message (after Stripe payment redirect)
  useEffect(() => {
    if (searchParams.get('enrolled') === 'true') {
      // Track payment completed (no sensitive payment data - just that it happened)
      trackEvent(AnalyticsEvents.PAYMENT_COMPLETED, {
        source: 'stripe_redirect',
      });
      toast.success('Successfully enrolled! Start learning now.');
      // Remove the query param
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('enrolled');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Fetch enrollments and meetings
  useEffect(() => {
    const fetchEnrollments = async () => {
      setIsLoading(true);
      try {
        const [enrollmentsRes, meetingsRes] = await Promise.all([
          enrollmentsApi.getAll(),
          meetingsApi.getUpcoming({ limit: 50 }),
        ]);
        
        const meetings = meetingsRes.data.success ? (meetingsRes.data.data || []) : [];
        
        if (enrollmentsRes.data.success) {
          const enrollmentsData = enrollmentsRes.data.data || [];
          
          // Enrich enrollments with active meeting info for live courses
          const enrichedEnrollments: EnrollmentWithMeeting[] = enrollmentsData.map(enrollment => {
            if (enrollment.course.courseType === 'LIVE') {
              // Find active (LIVE) or next scheduled meeting for this course
              const activeMeeting = meetings.find(
                (m: MeetingData) => m.course.id === enrollment.course.id && m.status === 'LIVE'
              ) || meetings.find(
                (m: MeetingData) => m.course.id === enrollment.course.id && m.status === 'SCHEDULED'
              );
              return { ...enrollment, activeMeeting };
            }
            return enrollment;
          });
          
          setEnrollments(enrichedEnrollments);
        }
      } catch (error) {
        console.error('Failed to fetch enrollments:', error);
        toast.error('Failed to load your courses');
      } finally {
        setIsLoading(false);
      }
    };
    fetchEnrollments();
  }, []);

  // Filter enrollments based on tab and search
  useEffect(() => {
    let filtered = [...enrollments];

    // Filter by status
    if (activeTab !== 'all') {
      filtered = filtered.filter((e) => e.status === activeTab);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (e) =>
          e.course.title.toLowerCase().includes(query) ||
          `${e.course.professor.firstName} ${e.course.professor.lastName}`
            .toLowerCase()
            .includes(query)
      );
    }

    setFilteredEnrollments(filtered);
  }, [enrollments, activeTab, searchQuery]);

  // Debounced search handler
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((value: string) => {
      setSearchQuery(value);
    }, 300),
    []
  );

  // Handle cancel enrollment
  const handleCancelEnrollment = async () => {
    if (!cancelModal) return;

    setIsCancelling(true);
    try {
      const response = await enrollmentsApi.cancel(cancelModal.id);
      if (response.data.success) {
        toast.success('Enrollment cancelled successfully');
        // Update local state
        setEnrollments((prev) =>
          prev.map((e) =>
            e.id === cancelModal.id ? { ...e, status: 'CANCELLED' as const } : e
          )
        );
        setCancelModal(null);
      }
    } catch (error: unknown) {
      console.error('Failed to cancel enrollment:', error);
      const message =
        (error as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Failed to cancel enrollment';
      toast.error(message);
    } finally {
      setIsCancelling(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return <Trophy className="w-4 h-4" />;
      case 'CANCELLED':
        return <XCircle className="w-4 h-4" />;
      default:
        return <Play className="w-4 h-4" />;
    }
  };

  const stats = {
    total: enrollments.length,
    active: enrollments.filter((e) => e.status === 'ACTIVE').length,
    completed: enrollments.filter((e) => e.status === 'COMPLETED').length,
    cancelled: enrollments.filter((e) => e.status === 'CANCELLED').length,
  };

  const tabs: { key: TabStatus; label: string; count: number }[] = [
    { key: 'all', label: 'All Courses', count: stats.total },
    { key: 'ACTIVE', label: 'In Progress', count: stats.active },
    { key: 'COMPLETED', label: 'Completed', count: stats.completed },
    { key: 'CANCELLED', label: 'Cancelled', count: stats.cancelled },
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-heading font-bold text-white tracking-tight">
          My Courses
        </h1>
        <p className="text-white/40 mt-1">
          Track your learning progress and continue where you left off
        </p>
      </div>

      {/* Stats Cards */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4"
      >
        {[
          { label: 'Total Enrolled', value: stats.total, color: '#3b82f6', icon: BookOpen },
          { label: 'In Progress', value: stats.active, color: '#f59e0b', icon: Play },
          { label: 'Completed', value: stats.completed, color: '#10b981', icon: CheckCircle2 },
          { label: 'Cancelled', value: stats.cancelled, color: '#ef4444', icon: XCircle },
        ].map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            className="p-5 rounded-2xl"
            style={{
              background: `linear-gradient(135deg, ${stat.color}15 0%, ${stat.color}08 100%)`,
              border: `1px solid ${stat.color}20`,
            }}
          >
            <div className="flex items-center gap-2 sm:gap-3">
              <div
                className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: `${stat.color}25` }}
              >
                <stat.icon className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: stat.color }} />
              </div>
              <div className="min-w-0">
                <div className="text-xl sm:text-2xl font-bold text-white">{stat.value}</div>
                <div className="text-[10px] sm:text-xs text-white/40 truncate">{stat.label}</div>
              </div>
            </div>
          </motion.div>
        ))}
      </motion.div>

      {/* Tabs and Search */}
      <div className="flex flex-col gap-4">
        {/* Tabs - Scrollable on mobile */}
        <div className="overflow-x-auto scrollbar-hide -mx-6 px-6 lg:mx-0 lg:px-0">
          <div className="flex gap-1 p-1 rounded-xl bg-white/[0.02] border border-white/[0.04] min-w-max lg:min-w-0 lg:w-fit">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 
                          flex items-center gap-1.5 sm:gap-2 whitespace-nowrap ${
                            activeTab === tab.key
                              ? 'bg-white/[0.08] text-white'
                              : 'text-white/40 hover:text-white/60 hover:bg-white/[0.02]'
                          }`}
              >
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="sm:hidden">{tab.label.replace('All Courses', 'All').replace('In Progress', 'Active')}</span>
                <span
                  className={`px-1.5 py-0.5 rounded-md text-xs ${
                    activeTab === tab.key
                      ? 'bg-white/10 text-white'
                      : 'bg-white/[0.04] text-white/40'
                  }`}
                >
                  {tab.count}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Search */}
        <div className="relative w-full sm:w-72">
          <input
            type="text"
            placeholder="Search courses..."
            onChange={(e) => debouncedSearch(e.target.value)}
            className="w-full h-11 pl-11 pr-4 rounded-xl text-white placeholder:text-white/30
                     bg-white/[0.02] border border-white/[0.06] 
                     focus:border-white/20 focus:outline-none focus:ring-0
                     transition-colors text-sm"
          />
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
        </div>
      </div>

      {/* Course List */}
      {isLoading ? (
        <SectionLoader />
      ) : filteredEnrollments.length > 0 ? (
        <div className="space-y-4">
          {filteredEnrollments.map((enrollment, index) => {
            const enrolledAt = new Date(enrollment.enrolledAt);
            const now = new Date();
            const hasStarted = Number(enrollment.progress) > 0;
            const canCancel =
              enrollment.status === 'ACTIVE' &&
              (now.getTime() - enrolledAt.getTime()) / (1000 * 60 * 60) <= 24 &&
              !hasStarted;
            const isLiveCourse = enrollment.course.courseType === 'LIVE';
            const activeMeeting = enrollment.activeMeeting;
            const isLive = activeMeeting?.status === 'LIVE';
            const hasScheduledMeeting = activeMeeting?.status === 'SCHEDULED';

            // For live courses, always link to course details (where sessions are shown)
            // For recorded courses, link to course player if active
            const courseLink = isLiveCourse
              ? `/courses/${enrollment.course.slug}`
              : enrollment.status === 'ACTIVE'
                ? `/student/course/${enrollment.id}`
                : `/courses/${enrollment.course.slug}`;

            const handleJoinMeeting = async (e: React.MouseEvent) => {
              e.preventDefault();
              e.stopPropagation();
              if (activeMeeting) {
                setJoiningMeetingId(activeMeeting.id);
                try {
                  await joinMeetingSecure(activeMeeting.id);
                } catch {
                  window.open(buildJitsiJoinUrl(activeMeeting.jitsiRoomId), '_blank');
                } finally {
                  setJoiningMeetingId(null);
                }
              }
            };

            return (
              <motion.div
                key={enrollment.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03 }}
                className="group relative rounded-2xl overflow-hidden"
                style={{
                  background: isLiveCourse 
                    ? 'linear-gradient(135deg, rgba(147, 51, 234, 0.05) 0%, rgba(255,255,255,0.02) 100%)'
                    : 'rgba(255,255,255,0.02)',
                  border: isLiveCourse 
                    ? '1px solid rgba(147, 51, 234, 0.15)'
                    : '1px solid rgba(255,255,255,0.04)',
                }}
              >
                <Link
                  to={courseLink}
                  className="flex flex-col sm:flex-row gap-4 p-4 sm:p-5"
                >
                  {/* Thumbnail */}
                  <div className="relative w-full sm:w-48 h-32 sm:h-28 rounded-xl overflow-hidden flex-shrink-0">
                    {enrollment.course.thumbnailUrl ? (
                      <img
                        src={enrollment.course.thumbnailUrl}
                        alt={enrollment.course.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    ) : (
                      <div
                        className="w-full h-full flex items-center justify-center"
                        style={{
                          background:
                            'linear-gradient(145deg, rgba(220, 70, 74, 0.2) 0%, rgba(15, 16, 20, 0.8) 100%)',
                        }}
                      >
                        <BookOpen className="w-8 h-8 text-white/30" />
                      </div>
                    )}

                    {/* Progress Overlay - Only for recorded courses */}
                    {enrollment.status === 'ACTIVE' && !isLiveCourse && (
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent flex items-end p-3">
                        <div className="w-full">
                          <div className="flex justify-between text-xs mb-1">
                            <span className="text-white/70">Progress</span>
                            <span className="text-white font-medium">
                              {Math.round(Number(enrollment.progress))}%
                            </span>
                          </div>
                          <div className="h-1.5 bg-white/20 rounded-full overflow-hidden">
                            <motion.div
                              initial={{ width: 0 }}
                              animate={{ width: `${enrollment.progress}%` }}
                              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                              className="h-full rounded-full"
                              style={{
                                background:
                                  'linear-gradient(90deg, #dc464a 0%, #f87171 100%)',
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Live Course Overlay */}
                    {enrollment.status === 'ACTIVE' && isLiveCourse && (
                      <div className={`absolute inset-0 bg-gradient-to-t ${isLive ? 'from-red-900/90' : 'from-black/80'} via-transparent to-transparent flex items-end p-3`}>
                        {isLive ? (
                          <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-red-500/30 border border-red-500/50 animate-pulse">
                            <Radio className="w-3 h-3 text-red-400" />
                            <span className="text-red-300 text-xs font-medium">LIVE NOW</span>
                          </div>
                        ) : hasScheduledMeeting && activeMeeting ? (
                          <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-amber-500/20 border border-amber-500/30">
                            <Calendar className="w-3 h-3 text-amber-400" />
                            <span className="text-amber-300 text-xs font-medium">
                              {new Date(activeMeeting.scheduledAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-purple-500/20 border border-purple-500/30">
                            <div className="w-2 h-2 rounded-full bg-purple-400" />
                            <span className="text-purple-300 text-xs font-medium">Live Sessions</span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Completed Badge */}
                    {enrollment.status === 'COMPLETED' && (
                      <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/20 border border-emerald-500/30">
                          <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                          <span className="text-emerald-400 text-sm font-medium">
                            Completed
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Course Info */}
                  <div className="flex-1 min-w-0 flex flex-col justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span
                          className={`badge text-[10px] ${getLevelColor(
                            enrollment.course.level || 'BEGINNER'
                          )}`}
                        >
                          {enrollment.course.level || 'BEGINNER'}
                        </span>
                        <span
                          className={`badge text-[10px] flex items-center gap-1 ${getStatusColor(
                            enrollment.status
                          )}`}
                        >
                          {getStatusIcon(enrollment.status)}
                          {enrollment.status}
                        </span>
                        {/* Course Type Badge */}
                        {isLiveCourse ? (
                          <span className="badge text-[10px] bg-purple-500/15 text-purple-400 border border-purple-500/25 flex items-center gap-1">
                            <Radio className="w-3 h-3" />
                            LIVE
                          </span>
                        ) : (
                          <span className="badge text-[10px] bg-blue-500/15 text-blue-400 border border-blue-500/25 flex items-center gap-1">
                            <Video className="w-3 h-3" />
                            VIDEO
                          </span>
                        )}
                        {canCancel && (
                          <span className="badge text-[10px] bg-amber-500/15 text-amber-400 border border-amber-500/25 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            Cancellable
                          </span>
                        )}
                      </div>

                      <h3 className="font-semibold text-white text-lg leading-tight group-hover:text-coral transition-colors line-clamp-1">
                        {enrollment.course.title}
                      </h3>

                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs sm:text-sm text-white/40">
                        <span className="truncate max-w-[200px]">
                          by {enrollment.course.professor.firstName}{' '}
                          {enrollment.course.professor.lastName}
                        </span>
                        <span className="hidden sm:block w-1 h-1 rounded-full bg-white/20" />
                        <span>{enrollment.course.totalLessons} lessons</span>
                        {enrollment.course.totalDuration && (
                          <>
                            <span className="hidden sm:block w-1 h-1 rounded-full bg-white/20" />
                            <span className="hidden sm:inline">{formatMinutes(enrollment.course.totalDuration)}</span>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center justify-between mt-3">
                      <p className="text-xs text-white/30">
                        Enrolled {formatRelativeTime(enrollment.enrolledAt)}
                        {enrollment.completedAt && (
                          <span className="ml-2">
                            • Completed {formatRelativeTime(enrollment.completedAt)}
                          </span>
                        )}
                      </p>

                      {enrollment.status === 'ACTIVE' && (
                        isLive && activeMeeting ? (
                          <button
                            onClick={handleJoinMeeting}
                            disabled={joiningMeetingId === activeMeeting.id}
                            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold 
                                     bg-red-500 text-white hover:bg-red-600 transition-colors
                                     disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {joiningMeetingId === activeMeeting.id ? (
                              <>
                                <InlineLoader />
                                Connecting...
                              </>
                            ) : (
                              <>
                                <Radio className="w-4 h-4" />
                                Join Live
                              </>
                            )}
                          </button>
                        ) : (
                          <div className={`flex items-center gap-2 text-sm font-medium group-hover:gap-3 transition-all ${
                            isLiveCourse ? 'text-purple-400' : 'text-coral'
                          }`}>
                            {isLiveCourse ? (
                              hasScheduledMeeting && activeMeeting ? (
                                <>
                                  <Clock className="w-4 h-4" />
                                  {new Date(activeMeeting.scheduledAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </>
                              ) : (
                                <>
                                  <Calendar className="w-4 h-4" />
                                  View Sessions
                                </>
                              )
                            ) : (
                              <>
                                Continue
                                <ChevronRight className="w-4 h-4" />
                              </>
                            )}
                          </div>
                        )
                      )}
                    </div>
                  </div>
                </Link>

                {/* Menu */}
                <div className="absolute top-4 right-4">
                  <CourseMenu
                    enrollment={enrollment}
                    onCancel={() => setCancelModal(enrollment)}
                    isOpen={openMenuId === enrollment.id}
                    onToggle={() =>
                      setOpenMenuId(openMenuId === enrollment.id ? null : enrollment.id)
                    }
                  />
                </div>
              </motion.div>
            );
          })}
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center py-20"
        >
          <div
            className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6"
            style={{ background: 'rgba(255,255,255,0.02)' }}
          >
            <BookOpen className="w-10 h-10 text-white/20" />
          </div>
          <h3 className="text-xl font-semibold text-white mb-2">
            {searchQuery ? 'No courses found' : 'No courses yet'}
          </h3>
          <p className="text-white/40 mb-6 max-w-sm mx-auto">
            {searchQuery
              ? 'Try adjusting your search or filters'
              : "You haven't enrolled in any courses. Start your learning journey today!"}
          </p>
          {!searchQuery && (
            <Link
              to="/courses"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-white
                       transition-all duration-300 hover:scale-105"
              style={{
                background: 'linear-gradient(135deg, #dc464a 0%, #c63b3f 100%)',
                boxShadow: '0 4px 20px rgba(220, 70, 74, 0.3)',
              }}
            >
              Browse Courses
              <ChevronRight className="w-4 h-4" />
            </Link>
          )}
        </motion.div>
      )}

      {/* Cancel Modal */}
      <AnimatePresence>
        {cancelModal && (
          <CancelModal
            enrollment={cancelModal}
            onClose={() => setCancelModal(null)}
            onConfirm={handleCancelEnrollment}
            isLoading={isCancelling}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
